import { useMemo } from 'react';
import { personalSessionKey } from '../hooks/useGateway';
import { agentIdFromSessionKey, useAppStore } from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import { filterAgentsForAllowedList } from '../utils/agentVisibility';
import GearIcon from './icons/GearIcon';

interface CollapsedRailProps {
  side: 'left' | 'right';
  onExpand: () => void;
  onSelectSession: (sessionKey: string) => Promise<void> | void;
}

export default function CollapsedRail({ side, onExpand, onSelectSession }: CollapsedRailProps) {
  const openSettings = useAppStore((state) => state.openSettings);
  const agents = useAppStore((state) => state.agents);
  const selectedAgentId = useAppStore((state) => state.selectedAgentId);
  const setSelectedAgentId = useAppStore((state) => state.setSelectedAgentId);
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const currentView = useAppStore((state) => state.currentView);
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const allowedAgents = useAppStore((state) => state.allowedAgents);

  const borderClass = side === 'left' ? 'border-r' : 'border-l';
  const icon = side === 'left' ? '»' : '«';
  const title = side === 'left' ? 'Expand sidebar' : 'Expand activity';

  const pickableAgents = useMemo(() => {
    const AGENT_ORDER = ['main', 'coco', 'edge', 'yuki', 'spike', 'researcher', 'coder', 'btw'];
    const withIdentity = agents.filter((agent) => agent.identity != null);
    const filtered = filterAgentsForAllowedList(withIdentity, allowedAgents);
    return filtered.sort((a, b) => {
      const ai = AGENT_ORDER.indexOf(a.id);
      const bi = AGENT_ORDER.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [agents, allowedAgents]);

  const activeAgentId = activeSessionKey ? agentIdFromSessionKey(activeSessionKey) : null;
  const effectiveSelectedAgentId =
    (selectedAgentId && pickableAgents.some((agent) => agent.id === selectedAgentId) ? selectedAgentId : null)
    ?? (activeAgentId && pickableAgents.some((agent) => agent.id === activeAgentId) ? activeAgentId : null)
    ?? pickableAgents[0]?.id
    ?? null;

  const handleAgentSelect = async (agentId: string) => {
    setSelectedAgentId(agentId);
    setCurrentView('chat');

    const currentDisplayName = useSettingsStore.getState().settings.general.displayName.trim();

    await onSelectSession(personalSessionKey(agentId, currentDisplayName));
  };

  const handleDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleAssets = () => {
    import('../stores/fileEditorStore').then(({ useFileEditorStore }) => {
      useFileEditorStore.getState().openPanel();
    });
    setCurrentView('assets');
  };

  const handleNotes = () => {
    import('../stores/notesStore').then(({ useNotesStore }) => {
      useNotesStore.getState().togglePanel();
    });
  };

  const handleTeam = () => {
    setCurrentView('team');
  };

  return (
    <div
      className={`h-full flex flex-col items-center ${borderClass} pt-3`}
      style={{ background: 'var(--theme-card)', borderColor: 'var(--theme-border)' }}
    >
      {/* Expand button */}
      <button
        type="button"
        onClick={onExpand}
        className="collapsed-rail-btn"
        title={title}
      >
        {icon}
      </button>

      {/* Left rail: Private agent icons */}
      {side === 'left' && pickableAgents.length > 0 && (
        <>
          <div className="collapsed-rail-divider" />
          <div className="flex flex-col items-center gap-1 w-full px-1">
            {pickableAgents.map((agent) => {
              const emoji = agent.identity?.emoji ?? '';
              const name = agent.identity?.name ?? agent.id;
              const agentId = agent.id;
              const isActive = effectiveSelectedAgentId === agentId;
              return (
                <button
                  key={agentId}
                  type="button"
                  onClick={() => void handleAgentSelect(agentId)}
                  className={`collapsed-rail-btn${isActive ? ' active' : ''}`}
                  title={name}
                  style={isActive ? {
                    background: 'var(--theme-accent)',
                    color: 'var(--theme-primary)',
                    borderColor: 'var(--theme-primary-20)',
                  } : {}}
                >
                  {emoji ? (
                    <span style={{ fontSize: '1rem' }}>{emoji}</span>
                  ) : (
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      {name.slice(0, 2)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="flex-1" />

      {/* Left rail: Studio icons */}
      {side === 'left' && (
        <>
          <div className="collapsed-rail-divider" />
          <div className="flex flex-col items-center gap-1 w-full px-1 mb-3">
            {/* Notes */}
            <button
              type="button"
              onClick={handleNotes}
              className="collapsed-rail-btn"
              title="Notes"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
            </button>

            {/* Team */}
            <button
              type="button"
              onClick={handleTeam}
              className={`collapsed-rail-btn${currentView === 'team' ? ' active' : ''}`}
              title="Team Channel"
              style={currentView === 'team' ? {
                background: 'var(--theme-accent)',
                color: 'var(--theme-primary)',
                borderColor: 'var(--theme-primary-20)',
              } : {}}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </button>

            {/* Dashboard */}
            <button
              type="button"
              onClick={handleDashboard}
              className={`collapsed-rail-btn${currentView === 'dashboard' ? ' active' : ''}`}
              title="Dashboard"
              style={currentView === 'dashboard' ? {
                background: 'var(--theme-accent)',
                color: 'var(--theme-primary)',
                borderColor: 'var(--theme-primary-20)',
              } : {}}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>

            {/* Files */}
            <button
              type="button"
              onClick={handleAssets}
              className={`collapsed-rail-btn${currentView === 'assets' ? ' active' : ''}`}
              title="Files"
              style={currentView === 'assets' ? {
                background: 'var(--theme-accent)',
                color: 'var(--theme-primary)',
                borderColor: 'var(--theme-primary-20)',
              } : {}}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </button>

            {/* Settings */}
            <button
              type="button"
              onClick={openSettings}
              className="collapsed-rail-btn"
              title="Settings"
            >
              <GearIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      )}

      {/* Right rail: only settings at bottom */}
      {side === 'right' && (
        <button
          type="button"
          onClick={openSettings}
          className="collapsed-rail-btn mb-3"
          title="Settings"
        >
          <GearIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
